/**
 * „Ansicht als Rolle“ (nur lesen): reine Entscheidungen und Texte fürs Frontend (geprüft in tests/rightsGuard/viewAsRole.check.ts).
 * Durchgesetzt wird die Ansicht im Backend (shrd JwtStrategy/JwtGuard): Rechte = Rolle ∩ eigene, jede schreibende Anfrage
 * wird abgelehnt. Das Frontend zeigt nur das Banner, startet/beendet die Ansicht und erklärt die Ablehnung.
 * Keine Store-/Vue-Importe (die Prüfung läuft ohne Vue).
 */

export type ViewAsRole = { roleId: string; roleName: string; until: number };

/** localStorage: gesetzt, solange eine Ansicht läuft (nach Ablauf: erst beenden, dann neu anmelden) */
export const VIEW_AS_ROLE_FLAG_KEY = 'vanilla.viewAsRole';
/** _t der Ablehnung (ForbiddenExceptions.ViewAsRoleReadOnlyException) */
export const VIEW_AS_ROLE_READ_ONLY_TYPE = 'ViewAsRoleReadOnlyException';
export const VIEW_AS_ROLE_READ_ONLY_TEXT = 'In der Ansicht als Rolle kann nichts gespeichert werden.';
export const VIEW_AS_ROLE_ENDED_TEXT = 'Ansicht als Rolle beendet – du bist wieder in deinem Account.';
export const VIEW_AS_ROLE_EXPIRED_TEXT = 'Die Ansicht als Rolle ist abgelaufen – du bist wieder in deinem Account.';
/** 401 mitten in der Ansicht: abgelaufen, oder ein Dienst ist noch nicht auf die Ansicht vorbereitet (lehnt das Token ab) */
export const VIEW_AS_ROLE_INTERRUPTED_TEXT = 'Die Ansicht als Rolle wurde beendet (abgelaufen oder in diesem Bereich noch nicht möglich) – '
    + 'du bist wieder in deinem Account.';
export const VIEW_AS_ROLE_PERSONAL_HINT = 'Persönliche Bereiche (Heute, Mein Stundenplan, Abrechnung …) zeigen deine eigenen Daten, '
    + 'nicht die einer bestimmten Person mit dieser Rolle.';

export function isViewAsRole(value: unknown): value is ViewAsRole {
    const view = value as ViewAsRole;
    return !!view && typeof view === 'object' && typeof view.roleId === 'string' && typeof view.roleName === 'string'
        && typeof view.until === 'number' && Number.isFinite(view.until);
}

/** Antwort-Body einer abgelehnten schreibenden Anfrage in der Ansicht? */
export function isViewAsRoleReadOnlyBody(data: unknown): boolean {
    return !!data && typeof data === 'object' && (data as { _t?: unknown })._t === VIEW_AS_ROLE_READ_ONLY_TYPE;
}

/** Restminuten (aufgerundet, nie negativ) */
export function remainingMinutes(until: number, now: number): number {
    return Math.max(0, Math.ceil((until - now) / 60000));
}

export function remainingText(until: number, now: number): string {
    const minutes = remainingMinutes(until, now);
    if (minutes <= 0) {
        return 'läuft ab';
    }
    return `noch ${minutes} Min.`;
}

/** „14:35“ (Ortszeit Berlin) */
export function untilClock(until: number): string {
    return new Date(until).toLocaleTimeString('de-DE', {timeZone: 'Europe/Berlin', hour: '2-digit', minute: '2-digit'});
}

export function confirmTitle(roleName: string): string {
    return `Vanilla als „${roleName}“ ansehen?`;
}

export function confirmText(minutes: number): string {
    return `Du siehst Vanilla ${minutes} Minuten lang so wie eine Person mit dieser Rolle: dieselben Menüs, Daten und Sperren `
        + '(nie mehr als deine eigenen Rechte). Speichern und Senden sind gesperrt. Der Start wird im Rechte-Protokoll vermerkt. '
        + VIEW_AS_ROLE_PERSONAL_HINT;
}

/** Wie lange bis zum automatischen Beenden (ms, mindestens 0; null = kein Timer nötig, weil > 24 h) */
export function autoEndDelay(until: number, now: number): number | null {
    const delay = until - now;
    if (delay > 24 * 60 * 60 * 1000) {
        return null;
    }
    return Math.max(0, delay);
}

export function readViewAsRoleFlag(): boolean {
    try {
        return window.localStorage.getItem(VIEW_AS_ROLE_FLAG_KEY) === '1';
    } catch (e) {
        return false;
    }
}

export function writeViewAsRoleFlag(active: boolean) {
    try {
        if (active) {
            window.localStorage.setItem(VIEW_AS_ROLE_FLAG_KEY, '1');
        } else {
            window.localStorage.removeItem(VIEW_AS_ROLE_FLAG_KEY);
        }
    } catch (e) {
        // blocked storage: after expiry the login page appears instead of the automatic way back
    }
}
